import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  Plus, Trash2, Package, Navigation, MapPin, Moon, Check, ChevronsUpDown, 
  Search, PenLine, FileText, Loader2, Edit2, ChevronDown, ChevronUp, Route, 
  Wrench, Lock, Crown, Users, User, Phone, Mail, Calendar, Plane
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import CustomPackageBuilder from '../CustomPackageBuilder';
import ManualPackageBuilder from '../ManualPackageBuilder';
import DMCPackageBuilder from '../DMCPackageBuilder';
import { PassengerDetails, RoomConfiguration } from './shared';
import { cn } from '../../lib/utils';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Common country codes for travel business
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
];

// Searchable Route Combobox Component
const RouteSearchCombobox = ({ routes, value, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedRoute = routes.find(r => r.route_id === value);
  
  const filteredRoutes = useMemo(() => {
    if (!searchQuery) return routes;
    const query = searchQuery.toLowerCase();
    return routes.filter(route => 
      route.route_code?.toLowerCase().includes(query) ||
      route.route_name?.toLowerCase().includes(query) ||
      route.destination_group?.toLowerCase().includes(query) ||
      route.stops?.some(s => s.place_name?.toLowerCase().includes(query))
    );
  }, [routes, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white border-slate-200 h-auto min-h-[52px] py-3 px-4 hover:bg-slate-50 shadow-sm"
          data-testid="route-select"
        >
          {selectedRoute ? (
            <div className="flex flex-col items-start text-left flex-1">
              <span className="font-semibold text-slate-900">
                {selectedRoute.route_code} - {selectedRoute.route_name}
              </span>
              <span className="text-xs text-slate-500 mt-1">
                {selectedRoute.total_days}D/{selectedRoute.total_nights}N • {selectedRoute.stops?.map(s => s.place_name).join(' → ')}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <Search className="w-4 h-4" />
              <span>Search or select a route...</span>
            </div>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg border border-slate-200" align="start">
        <div className="flex items-center border-b border-slate-200 px-4 py-3 bg-slate-50">
          <Search className="mr-3 h-5 w-5 text-slate-400" />
          <input
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
            placeholder="Search routes by name, code, or destination..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="ml-2 text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          )}
        </div>
        
        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 bg-white">
          {filteredRoutes.length} route{filteredRoutes.length !== 1 ? 's' : ''} found
        </div>
        
        <div className="max-h-[350px] overflow-y-auto bg-white">
          {filteredRoutes.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No routes found matching "{searchQuery}"
            </div>
          ) : (
            filteredRoutes.map((route) => (
              <div
                key={route.route_id}
                data-testid={`route-option-${route.route_id}`}
                className={cn(
                  "flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-50 last:border-b-0",
                  value === route.route_id 
                    ? "bg-blue-50 border-l-4 border-l-blue-500" 
                    : "hover:bg-slate-50 border-l-4 border-l-transparent"
                )}
                onClick={() => {
                  onSelect(route.route_id);
                  setOpen(false);
                  setSearchQuery('');
                }}
              >
                <div className={cn(
                  "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5",
                  value === route.route_id 
                    ? "border-blue-500 bg-blue-500" 
                    : "border-slate-300"
                )}>
                  {value === route.route_id && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">
                      {route.route_code}
                    </span>
                    <span className="text-slate-600">
                      {route.route_name}
                    </span>
                    <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full shrink-0">
                      {route.total_days}D/{route.total_nights}N
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {route.stops?.map((s, idx) => (
                      <span key={idx} className="flex items-center">
                        {s.place_name} ({s.nights}N)
                        {idx < route.stops.length - 1 && <span className="mx-1">→</span>}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const GuestTripStep = ({
  formData,
  handleInputChange,
  routes,
  places,
  vehicleOptions,
  setFormData,
  addCustomStop,
  removeCustomStop,
  updateCustomStop,
  totalPax,
  suggestedSingleRooms,
  suggestedDoubleRooms,
  travelAgents = [],
  isB2BAgent,
  onPlacesRefresh,
}) => {
  const { hasFeature, subscriptionStatus, refreshFeatures } = useAuth();
  // Always check current plan access when the wizard opens
  useEffect(() => { refreshFeatures(); }, [refreshFeatures]);
  
  // Feature checks for quotation types
  const hasPredefinedAccess = hasFeature('quote_type_predefined');
  const hasCustomAccess = hasFeature('quote_type_custom');
  const hasManualAccess = hasFeature('quote_type_manual');
  const hasDMCAccess = hasFeature('quote_type_dmc');
  const HIDE_MANUAL_DMC_TABS = true; // Ticket #285275
  const hasDMCAIParseAccess = hasFeature('dmc_ai_parse');
  const hasDMCFileUploadAccess = hasFeature('dmc_file_upload');
  
  // Upgrade dialog state
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, feature: null, featureName: '' });
  
  // Feature info for upgrade prompts
  const FEATURE_INFO = {
    quote_type_predefined: { name: 'Predefined Routes', plan: 'Starter' },
    quote_type_custom: { name: 'Custom Package', plan: 'Growth' },
    quote_type_manual: { name: 'Manual Package', plan: 'Growth' },
    quote_type_dmc: { name: 'DMC Package', plan: 'Pro' },
  };
  
  const handleLockedTabClick = (featureKey) => {
    const info = FEATURE_INFO[featureKey] || { name: featureKey, plan: 'a higher' };
    setUpgradeDialog({ open: true, feature: featureKey, featureName: info.name, planName: info.plan });
  };

  // Room configuration state
  const [roomConfig, setRoomConfig] = useState({
    auto_calculate: true,
    single_rooms: 0,
    double_rooms: 1,
    triple_rooms: 0,
    adult_extra_beds: 0,
    children_with_bed: 0,
    children_without_bed: 0,
    infants: 0,
    explore_alternative_rooms: false,
  });

  // Route selection mode
  const [routeSelectionMode, setRouteSelectionMode] = useState(
    formData.route_type === 'custom' ? 'custom' : 'predefined'
  );

  useEffect(() => {
    if (formData.route_type === 'custom') {
      setRouteSelectionMode('custom');
    } else if (formData.route_type === 'predefined') {
      setRouteSelectionMode('predefined');
    }
  }, [formData.route_type]);

  const handleRouteSelectionModeChange = (mode) => {
    setRouteSelectionMode(mode);
    if (mode === 'custom') {
      handleInputChange('route_type', 'custom');
      handleInputChange('route_id', null);
      if (!formData.custom_route || formData.custom_route.length === 0) {
        setFormData(prev => ({
          ...prev,
          custom_route: [{ place_id: '', place_name: '', nights: 1, order_seq: 1 }]
        }));
      }
    } else {
      handleInputChange('route_type', 'predefined');
    }
  };

  // Sync room config with formData on mount
  useEffect(() => {
    if (formData.room_config) {
      setRoomConfig(prev => ({ ...prev, ...formData.room_config }));
    }
  }, []);

  // Update formData when room config changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      room_config: roomConfig,
      single_rooms: roomConfig.single_rooms,
      double_rooms: roomConfig.double_rooms,
      triple_rooms: roomConfig.triple_rooms,
      extra_beds: roomConfig.adult_extra_beds + roomConfig.children_with_bed,
    }));
  }, [roomConfig, setFormData]);

  // Auto-calculate room configuration
  useEffect(() => {
    if (roomConfig.auto_calculate) {
      const adults = formData.no_adults || 2;
      const childrenWithBed = formData.no_children_with_bed || 0;
      const childrenNoBed = formData.no_children_no_bed || 0;
      const infants = formData.no_infants || 0;
      
      let singleRooms = 0;
      let doubleRooms = 0;
      let tripleRooms = 0;
      let adultExtraBeds = 0;
      
      const pairs = Math.floor(adults / 2);
      const oddAdult = adults % 2;
      
      if (oddAdult === 1 && childrenWithBed >= 1) {
        tripleRooms = 1;
        doubleRooms = pairs;
        const remainingKidsWithBed = Math.max(0, childrenWithBed - 2);
        adultExtraBeds = 0;
      } else if (oddAdult === 1) {
        singleRooms = 1;
        doubleRooms = pairs;
        adultExtraBeds = 0;
      } else {
        doubleRooms = pairs;
      }
      
      setRoomConfig(prev => ({
        ...prev,
        single_rooms: singleRooms,
        double_rooms: doubleRooms,
        triple_rooms: tripleRooms,
        adult_extra_beds: adultExtraBeds,
        children_with_bed: childrenWithBed,
        children_without_bed: childrenNoBed,
        infants: infants,
      }));
    }
  }, [formData.no_adults, formData.no_children_with_bed, formData.no_children_no_bed, formData.no_infants, roomConfig.auto_calculate]);

  const calculateCustomRouteNights = () => {
    return formData.custom_route?.reduce((sum, stop) => sum + (stop.nights || 0), 0) || 0;
  };

  return (
    <div className="space-y-4" data-testid="guest-trip-step">
      {/* Section 1: Guest Information */}
      <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
        <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
          <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
            <User className="w-4 h-4" />
            Guest Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* B2B Agent Selection */}
          {travelAgents.length > 0 && !isB2BAgent && (
            <div className="p-3 sm:p-4 bg-[var(--blue-light)] rounded-lg border border-[rgba(37,99,235,0.15)] space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--blue)]" />
                <Label className="text-[var(--navy)] font-semibold text-xs uppercase">B2B Travel Agent (Optional)</Label>
              </div>
              <Select
                value={formData.travel_agent_id || 'none'}
                onValueChange={(v) => {
                  const agentId = v === 'none' ? null : v;
                  handleInputChange('travel_agent_id', agentId);
                  if (agentId) {
                    const agent = travelAgents.find(a => a.travel_agent_id === agentId);
                    if (agent) {
                      handleInputChange('guest_name', agent.agent_name || '');
                      handleInputChange('guest_phone', agent.phone || '');
                      handleInputChange('guest_email', agent.email || '');
                      if (agent.default_markup_type) {
                        const markupType = agent.default_markup_type.toLowerCase() === 'percent' ? 'percent' : 'amount';
                        handleInputChange('markup_type', markupType);
                        if (markupType === 'percent') {
                          handleInputChange('markup_pct', agent.default_markup_value || 0);
                        } else {
                          handleInputChange('markup_amount', agent.default_markup_value || 0);
                        }
                      }
                    }
                  } else {
                    handleInputChange('markup_type', 'percent');
                    handleInputChange('markup_pct', 20);
                  }
                }}
              >
                <SelectTrigger data-testid="travel-agent-select">
                  <SelectValue placeholder="Select B2B Agent (if applicable)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Direct Customer (No Agent)</SelectItem>
                  {travelAgents.map((agent) => (
                    <SelectItem key={agent.travel_agent_id} value={agent.travel_agent_id}>
                      {agent.agent_name} {agent.company_name ? `- ${agent.company_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Guest Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="guest_name" className="text-[11px] font-semibold text-[var(--g500)] uppercase">
                Guest Name
              </Label>
              <Input
                id="guest_name"
                key={`guest_name_${isB2BAgent ? 'partner' : 'internal'}`}
                defaultValue={formData.guest_name}
                onBlur={(e) => handleInputChange('guest_name', e.target.value)}
                placeholder="Enter guest name"
                data-testid="guest-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guest_phone" className="text-[11px] font-semibold text-[var(--g500)] uppercase">
                Phone (WhatsApp)
              </Label>
              <div className="flex gap-0">
                <Select
                  value={formData.guest_country_code || '+91'}
                  onValueChange={(v) => handleInputChange('guest_country_code', v)}
                >
                  <SelectTrigger className="w-[90px] rounded-r-none border-r-0 bg-[var(--g100)]" data-testid="country-code-select">
                    <SelectValue placeholder="+91" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="guest_phone"
                  key={`guest_phone_${isB2BAgent ? 'partner' : 'internal'}`}
                  defaultValue={formData.guest_phone}
                  onBlur={(e) => handleInputChange('guest_phone', e.target.value)}
                  placeholder="10 digit number"
                  className="flex-1 rounded-l-none"
                  type="tel"
                  data-testid="guest-phone-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guest_email" className="text-[11px] font-semibold text-[var(--g500)] uppercase">
                Email
              </Label>
              <Input
                id="guest_email"
                key={`guest_email_${isB2BAgent ? 'partner' : 'internal'}`}
                type="email"
                defaultValue={formData.guest_email}
                onBlur={(e) => handleInputChange('guest_email', e.target.value)}
                placeholder="guest@example.com"
                data-testid="guest-email-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Trip Selection */}
      <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
        <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
          <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
            <Route className="w-4 h-4" />
            Trip Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-5">
          {/* Route Type Tabs */}
          <Tabs 
            value={formData.route_type === 'custom' ? 'predefined' : formData.route_type} 
            onValueChange={(v) => {
              handleInputChange('route_type', v);
              if (v !== 'predefined' && v !== 'custom') {
                setRouteSelectionMode('predefined');
              }
            }}
          >
            <TabsList className={cn("grid w-full h-auto", HIDE_MANUAL_DMC_TABS ? "grid-cols-2" : "grid-cols-4")}>
              <TabsTrigger 
                value="predefined" 
                data-testid="predefined-route-tab" 
                className={cn(
                  "text-xs sm:text-sm px-1 sm:px-3 py-2 whitespace-nowrap relative",
                  !hasPredefinedAccess && "opacity-60"
                )}
                disabled={!hasPredefinedAccess}
                onClick={(e) => {
                  if (!hasPredefinedAccess) {
                    e.preventDefault();
                    handleLockedTabClick('quote_type_predefined');
                  }
                }}
              >
                <span className="hidden sm:inline">Predefined</span>
                <span className="sm:hidden">Predef</span>
                {!hasPredefinedAccess && <Lock className="w-3 h-3 ml-1 inline" />}
              </TabsTrigger>
              <TabsTrigger 
                value="custom_package" 
                data-testid="custom-package-tab" 
                className={cn(
                  "flex items-center gap-1 text-xs sm:text-sm px-1 sm:px-3 py-2 whitespace-nowrap",
                  !hasCustomAccess && "opacity-60"
                )}
                disabled={!hasCustomAccess}
                onClick={(e) => {
                  if (!hasCustomAccess) {
                    e.preventDefault();
                    handleLockedTabClick('quote_type_custom');
                  }
                }}
              >
                <span className="hidden sm:inline">Custom</span>
                <span className="sm:hidden">Custom</span>
                {!hasCustomAccess && <Lock className="w-3 h-3 ml-1 inline" />}
              </TabsTrigger>
              {/* Ticket #285275: Manual and DMC package types hidden for now (set false to restore) */}
              {!HIDE_MANUAL_DMC_TABS && (
              <>
              <TabsTrigger 
                value="manual_package" 
                data-testid="manual-package-tab" 
                className={cn(
                  "flex items-center gap-1 text-xs sm:text-sm px-1 sm:px-3 py-2 whitespace-nowrap",
                  !hasManualAccess && "opacity-60"
                )}
                disabled={!hasManualAccess}
                onClick={(e) => {
                  if (!hasManualAccess) {
                    e.preventDefault();
                    handleLockedTabClick('quote_type_manual');
                  }
                }}
              >
                <span className="hidden sm:inline">Manual</span>
                <span className="sm:hidden">Manual</span>
                {!hasManualAccess && <Lock className="w-3 h-3 ml-1 inline" />}
              </TabsTrigger>
              <TabsTrigger 
                value="dmc_package" 
                data-testid="dmc-package-tab" 
                className={cn(
                  "flex items-center gap-1 text-xs sm:text-sm px-1 sm:px-3 py-2 whitespace-nowrap",
                  !hasDMCAccess && "opacity-60"
                )}
                disabled={!hasDMCAccess}
                onClick={(e) => {
                  if (!hasDMCAccess) {
                    e.preventDefault();
                    handleLockedTabClick('quote_type_dmc');
                  }
                }}
              >
                <span className="hidden sm:inline">DMC</span>
                <span className="sm:hidden">DMC</span>
                {!hasDMCAccess && <Lock className="w-3 h-3 ml-1 inline" />}
              </TabsTrigger>
              </>
              )}
            </TabsList>

            {/* Predefined Route Content */}
            <TabsContent value="predefined" className="mt-4 space-y-4">
              {/* Toggle between Predefined and Custom Route */}
              <div className="flex items-center gap-2 mb-4">
                <Button
                  type="button"
                  variant={routeSelectionMode === 'predefined' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleRouteSelectionModeChange('predefined')}
                  data-testid="route-mode-predefined-btn"
                  className={cn(
                    "flex-1 sm:flex-none",
                    routeSelectionMode === 'predefined' && "bg-navy hover:bg-navy/90"
                  )}
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Select Route
                </Button>
                <Button
                  type="button"
                  variant={routeSelectionMode === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleRouteSelectionModeChange('custom')}
                  data-testid="route-mode-custom-btn"
                  className={cn(
                    "flex-1 sm:flex-none",
                    routeSelectionMode === 'custom' && "bg-navy hover:bg-navy/90"
                  )}
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  Build Custom
                </Button>
              </div>

              {/* Predefined Route Selection */}
              {routeSelectionMode === 'predefined' && (
                <div className="space-y-4">
                  <RouteSearchCombobox
                    routes={routes}
                    value={formData.route_id}
                    onSelect={(routeId) => {
                      handleInputChange('route_id', routeId);
                      const selectedRoute = routes.find(r => r.route_id === routeId);
                      if (selectedRoute) {
                        handleInputChange('transfer_days', selectedRoute.total_days);
                        handleInputChange('custom_route', selectedRoute.stops?.map((s, idx) => ({
                          place_id: s.place_id,
                          place_name: s.place_name,
                          nights: s.nights,
                          order_seq: idx + 1
                        })) || []);
                        // Auto-fill arrival and departure cities from route's dedicated fields (not stops)
                        // Routes have arrival_city (pickup point like airport) and departure_city (drop point)
                        // These are different from the tour stops
                        handleInputChange('arrival_city', selectedRoute.arrival_city || selectedRoute.stops?.[0]?.place_name || '');
                        handleInputChange('departure_city', selectedRoute.departure_city || selectedRoute.stops?.[selectedRoute.stops.length - 1]?.place_name || '');
                      }
                    }}
                  />
                  
                  {/* Selected Route Summary */}
                  {formData.route_id && routes.find(r => r.route_id === formData.route_id) && (
                    <div className="p-3 bg-[var(--blue-light)] rounded-lg border border-[rgba(37,99,235,0.15)]">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-[var(--blue)]" />
                        <span className="font-semibold text-[var(--navy)] text-sm">Selected Route</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        {routes.find(r => r.route_id === formData.route_id)?.stops?.map((s, idx, arr) => (
                          <span key={idx}>
                            <span className="font-medium">{s.place_name}</span>
                            <span className="text-slate-400"> ({s.nights}N)</span>
                            {idx < arr.length - 1 && <span className="mx-2">→</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Route Builder */}
              {routeSelectionMode === 'custom' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--navy)]">Build Your Route</span>
                      <span className="text-xs bg-navy text-white px-2 py-0.5 rounded">
                        {calculateCustomRouteNights()}N / {calculateCustomRouteNights() + 1}D
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCustomStop}
                      data-testid="custom-stop-add-btn"
                      className="h-8 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Stop
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {(formData.custom_route || []).map((stop, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <span className="w-6 h-6 flex items-center justify-center bg-navy text-white text-xs rounded-full">
                          {index + 1}
                        </span>
                        <SearchableSelect
                          options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                          value={stop.place_name || ""}
                          onValueChange={(v) => {
                            const place = places.find(p => p.place_name === v);
                            updateCustomStop(index, { 
                              place_name: v, 
                              place_id: place?.place_id || '' 
                            });
                          }}
                          placeholder="Select place"
                          searchPlaceholder="Search..."
                          className="flex-1 bg-white"
                          data-testid={`custom-stop-place-${index}`}
                        />
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="1"
                            value={stop.nights || 1}
                            onChange={(e) => updateCustomStop(index, { nights: parseInt(e.target.value) || 1 })}
                            data-testid={`custom-stop-nights-${index}`}
                            className="w-16 text-center"
                          />
                          <span className="text-xs text-slate-500">N</span>
                        </div>
                        {(formData.custom_route?.length || 0) > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCustomStop(index)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Custom Package Content - Now just shows a message, actual builder moved below */}
            <TabsContent value="custom_package" className="mt-4">
              <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p>Fill in Travel Dates and Passengers below, then configure your custom package destinations.</p>
              </div>
            </TabsContent>

            {/* Manual Package Content - Message, actual builder moved below */}
            <TabsContent value="manual_package" className="mt-4">
              <div className="text-sm text-slate-500 bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <p><strong>Manual Package</strong> - Enter hotels, rooms, and costs manually without rate masters. Perfect for Travel Agents.</p>
              </div>
            </TabsContent>

            {/* DMC Package Content - Now just shows a message, actual builder moved below */}
            <TabsContent value="dmc_package" className="mt-4">
              <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p>Fill in Travel Dates and Passengers below, then upload or paste your DMC package content.</p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Section 3: Travel Dates (For all route types) */}
      {(formData.route_type === 'predefined' || formData.route_type === 'custom' || formData.route_type === 'custom_package' || formData.route_type === 'manual_package' || formData.route_type === 'dmc_package') && (
        <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
          <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
            <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Travel Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Arrival City</Label>
                {(formData.route_type === 'manual_package' || formData.route_type === 'dmc_package') ? (
                  <Input
                    type="text"
                    placeholder="Enter arrival city"
                    value={formData.arrival_city || ""}
                    onChange={(e) => handleInputChange('arrival_city', e.target.value)}
                    className="bg-white"
                    data-testid="arrival-city-input"
                  />
                ) : (
                  <SearchableSelect
                    options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                    value={formData.arrival_city || ""}
                    onValueChange={(v) => handleInputChange('arrival_city', v)}
                    placeholder="Select city"
                    searchPlaceholder="Search cities..."
                    className="bg-white"
                    data-testid="arrival-city-input"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Arrival Date <span className="text-[var(--err)]">*</span></Label>
                <Input
                  type="date"
                  value={formData.arrival_date}
                  onChange={(e) => handleInputChange('arrival_date', e.target.value)}
                  data-testid="arrival-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Arrival Point</Label>
                {(formData.route_type === 'manual_package' || formData.route_type === 'dmc_package') ? (
                  <Input
                    type="text"
                    placeholder="e.g., Airport, Station"
                    value={formData.arrival_point || ""}
                    onChange={(e) => handleInputChange('arrival_point', e.target.value)}
                    className="bg-white"
                    data-testid="arrival-point-input"
                  />
                ) : (
                  <Select
                    value={formData.arrival_point}
                    onValueChange={(v) => handleInputChange('arrival_point', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Airport">Airport</SelectItem>
                      <SelectItem value="Railway Station">Railway Station</SelectItem>
                      <SelectItem value="Bus Stand">Bus Stand</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-[var(--g500)] uppercase">Departure City</Label>
                {(formData.route_type === 'manual_package' || formData.route_type === 'dmc_package') ? (
                  <Input
                    type="text"
                    placeholder="Enter departure city"
                    value={formData.departure_city || ""}
                    onChange={(e) => handleInputChange('departure_city', e.target.value)}
                    className="bg-white"
                    data-testid="departure-city-input"
                  />
                ) : (
                  <SearchableSelect
                    options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                    value={formData.departure_city || ""}
                    onValueChange={(v) => handleInputChange('departure_city', v)}
                    placeholder="Select city"
                    searchPlaceholder="Search cities..."
                    className="bg-white"
                    data-testid="departure-city-input"
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 4: Passengers & Rooms (For all route types) */}
      {(formData.route_type === 'predefined' || formData.route_type === 'custom' || formData.route_type === 'custom_package' || formData.route_type === 'manual_package' || formData.route_type === 'dmc_package') && (
        <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
          <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
            <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
              <Users className="w-4 h-4" />
              Passengers & Rooms
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-5">
            <PassengerDetails
              noAdults={formData.no_adults}
              noChildrenWithBed={formData.no_children_with_bed}
              noChildrenNoBed={formData.no_children_no_bed}
              noInfants={formData.no_infants}
              onAdultsChange={(val) => handleInputChange('no_adults', val)}
              onChildrenWithBedChange={(val) => handleInputChange('no_children_with_bed', val)}
              onChildrenNoBedChange={(val) => handleInputChange('no_children_no_bed', val)}
              onInfantsChange={(val) => handleInputChange('no_infants', val)}
              suggestedSingleRooms={suggestedSingleRooms}
              suggestedDoubleRooms={suggestedDoubleRooms}
              showSummary={true}
            />

            <RoomConfiguration
              roomConfig={roomConfig}
              onRoomConfigChange={setRoomConfig}
              showExtendedBeds={true}
              compact={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Section 5: Custom Package Builder (Only for Custom Package) */}
      {formData.route_type === 'custom_package' && (
        <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
          <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
            <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Build Your Package
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <CustomPackageBuilder
              places={places}
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
            />
          </CardContent>
        </Card>
      )}

      {/* Section 6: Manual Package Builder (Only for Manual Package) */}
      {formData.route_type === 'manual_package' && (
        <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
          <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
            <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
              <PenLine className="w-4 h-4" />
              Build Manual Package
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <ManualPackageBuilder
              formData={formData}
              setFormData={setFormData}
            />
          </CardContent>
        </Card>
      )}

      {/* Section 7: DMC Package Content (Only for DMC Package) */}
      {formData.route_type === 'dmc_package' && (
        <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]">
          <CardHeader className="py-3 px-4 sm:px-6 border-b border-[var(--g200)]">
            <CardTitle className="text-sm sm:text-base font-bold text-[var(--navy)] flex items-center gap-2">
              <FileText className="w-4 h-4" />
              DMC Package Content
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <DMCPackageBuilder
              formData={formData}
              handleInputChange={handleInputChange}
              setFormData={setFormData}
              hasAIParseAccess={hasDMCAIParseAccess}
              hasFileUploadAccess={hasDMCFileUploadAccess}
            />
          </CardContent>
        </Card>
      )}

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialog.open} onOpenChange={(open) => setUpgradeDialog({ ...upgradeDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Upgrade Required
            </DialogTitle>
            <DialogDescription className="text-left">
              <strong>{upgradeDialog.featureName}</strong> is available in the <strong>{upgradeDialog.planName}</strong> plan and above.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
              <p className="text-sm text-slate-600 mb-3">
                Upgrade your subscription to unlock this feature and many more:
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Access to {upgradeDialog.featureName}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Higher quotation limits</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Priority support</span>
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setUpgradeDialog({ ...upgradeDialog, open: false })}>
              Maybe Later
            </Button>
            <Button 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={() => {
                setUpgradeDialog({ ...upgradeDialog, open: false });
                window.location.href = '/settings/subscription';
              }}
            >
              View Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestTripStep;
